import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Divider,
  Avatar,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

// STYLED BUTTON
const GradientButton = styled(Button)({
  background: "linear-gradient(180deg, #6BD0F6, #00AEEF)",
  color: "#FFFFFF",
  fontWeight: "bold",
  textTransform: "none",
  borderRadius: 6,
  padding: "8px 24px",
  "&:hover": { background: "linear-gradient(180deg, #5BC0E6, #009ADF)" },
});

export default function ProjectDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch specific project
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await axios.get(
          `https://backend-global-1vq9.onrender.com/api/projects/${id}`
        );
        setProject(res.data);
      } catch (err) {
        console.error("Error fetching project:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: "60vh" }}
      >
        <CircularProgress sx={{ color: "#00AEEF" }} />
      </Box>
    );
  }

  if (!project) {
    return (
      <Typography textAlign="center" mt={5}>
        Project not found.
      </Typography>
    );
  }

  return (
    <Box sx={{ background: "#F6F7F9", minHeight: "100vh" }}>
      <TopBar />

      {/* HEADER */}
      <Typography
        variant="h3"
        sx={{ color: "#010101", ml: "267px", mt: 4, fontWeight: "bold" }}
      >
        Project Details
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 3,
          mb: 5,
          paddingX: "100px",
        }}
      >
        <Card
          elevation={3}
          sx={{
            width: "100%",
            maxWidth: "900px",
            borderRadius: 4,
            padding: 3,
            bgcolor: "#ffffff",
          }}
        >
          <CardContent>
            {/* HEADER ROW */}
            <Box display="flex" gap={2} alignItems="center">
              <Avatar
                variant="rounded"
                sx={{
                  width: 70,
                  height: 70,
                  bgcolor: "#E8F4FF",
                  color: "#007BCE",
                }}
              >
                <InsertDriveFileIcon sx={{ fontSize: 35 }} />
              </Avatar>

              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {project.title}
                </Typography>

                <Typography sx={{ color: "#6C6C6C" }}>
                  {project.category}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* BASIC DETAILS */}
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Status:</strong> {project.status}
            </Typography>

            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Completion:</strong> {project.completion}%
            </Typography>

            <LinearProgress
              variant="determinate"
              value={project.completion}
              sx={{ height: 10, borderRadius: 2, mb: 3 }}
            />

            {/* DESCRIPTION */}
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
              Description
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {project.description}
            </Typography>

            {/* DATES */}
            <Typography variant="body2" sx={{ color: "#777" }}>
              <strong>Created:</strong>{" "}
              {new Date(project.createdAt).toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "#777" }}>
              <strong>Updated:</strong>{" "}
              {new Date(project.updatedAt).toLocaleString()}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* ACTION BUTTONS */}
            <Box display="flex" justifyContent="space-between">
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{ textTransform: "none", fontWeight: "bold" }}
              >
                Back
              </Button>

              <GradientButton onClick={() => navigate("/createProjects")}>
                Edit Project
              </GradientButton>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Footer />
    </Box>
  );
}
